import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withStyles } from '@material-ui/core/styles';
import { setSearchState, appNotify, setChannelWorkflowGridStateUpdate, setPostAcqWorkflowGridStateUpdate, fetchSearchLastPricedByMe } from 'store/actions';
import { push } from 'connected-react-router';
import {
  Paper,
  InputBase,
  IconButton,
  Typography,
  Drawer,
  Tooltip,
  Chip
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import PostAddIcon from '@material-ui/icons/PostAdd';
import { Constants, getModuleByLocation } from 'utilities/constants';
import { withRouter } from 'react-router-dom';
import { LoadingOverlayWrapper } from 'components/layout';
import { SearchCard } from 'components/search';
import debounce from 'lodash/debounce';
import classNames from 'classnames';

const SearchDrawer = ({ classes, module, searchIsOpen, setSearchState, searchValue, data, searchBoxValue, resultsLimited, importCount, propertyCount, auctionCount, loading, appNotify,
  isSearchingLastPriced, push, setChannelWorkflowGridStateUpdate, fetchSearchLastPricedByMe, channelWorkflowGridDataUpdate }) => {

  useEffect(() => { // Retrigger fetch on search open
    if (!searchIsOpen) return;
    if (searchBoxValue) {
      setSearchState({ searchValue: searchBoxValue }); // Set value again to retrigger fetch
    } else if (isSearchingLastPriced) {
      fetchSearchLastPricedByMe();
    }
  }, [searchIsOpen])
  
  // setSearchState has the current module baked in; keep the debounced dispatcher rebound to it
  // so switching modules doesn't keep firing against the module the drawer first mounted in.
  const debouncedOnSearchChange = useCallback(debounce(value => setSearchState({ searchValue: value }), 500), [setSearchState]);
    
  useEffect(() => {
    if (searchValue !== searchBoxValue) {
      debouncedOnSearchChange(searchBoxValue);
    }
  }, [searchBoxValue, searchValue])

  const drawerOnClose = () => {
    setSearchState({ searchIsOpen: false });

    // Close modal and clear search results if user clicked My Last 100 Priced
    if (data.length > 0 && searchBoxValue === '') {
      setSearchState({ searchIsOpen: false });
      debouncedOnSearchChange('');
    }
}
  const onSearchChange = e => {
    setSearchState({ searchBoxValue: e.target.value });
  };

  const pasteFromClipboard = async (e) => {
    // const result = await navigator.permissions.query({ name: 'clipboard-read' });
    // console.log(navigator.permissions)
    // If permission to read the clipboard is granted or if the user will
    // be prompted to allow it, we proceed.
    try {
      let data = await navigator.clipboard.readText();
      data = data.replace(/\s+/g, ',').replace(/,+/g, ',');
      // if (data.slice(-1) == ',') {
      // Remove comma at the end of string replace
      // var trimmedData = data.slice(0, -1);      
      var splitData = data.split(',');
      var dedupData = [...new Set(splitData)];
      var filteredData = dedupData.filter(str => {
        return !Number.isNaN(parseInt(str))
      });
      if (filteredData.length > 0) {
        var trimmedData = filteredData.join(",");
        setSearchState({ searchBoxValue: trimmedData });
      } else {
        setSearchState({ searchBoxValue: dedupData.join(",") });
      }
    } catch (err) {
      // alert('Paste Error: ' + JSON.stringify(err));
      // Snackbar instead of alert message, unsure how to reproduce this error. Maybe if clipboard permission is not allowed?
      //TODO: perhaps generate a nicer message to the user?
      appNotify(`Paste Error: ${JSON.stringify(err)}`, 'error');
    }
  }
  const cards = useMemo(() => {
    return data.map(d => {
      return (
        <SearchCard key={d.resultType + d.id} searchString={searchValue} {...d} />
      );
    });
  }, [data])
  const handleFilterChannelClick = (filteredChannelArr) => {
    let filterValue = [['id', 'anyof', filteredChannelArr.map(el => el.id)]];
    setChannelWorkflowGridStateUpdate({ filterValue: filterValue }, true);
    push(module === Constants.Module.POSTACQ ? Constants.Routes.POSTACQ_WORKFLOW : Constants.Routes.CHANNEL_WORKFLOW);
    drawerOnClose();
  }
  const filterChannels = useMemo(() => {
    // Group the primary result type by channel: properties in acq, tasks in post-acq (a given search returns one module's results).
    let filteredByPropertyType = data.filter(type => type.resultType === 'property' || type.resultType === 'task');
    let distinctChannels = new Set();
    filteredByPropertyType.forEach((d) => {
      distinctChannels.add(d.channel);
    });
    if (distinctChannels.size > 0) {
      let filteredDistinctChannels = Array.from(distinctChannels).map(chan => {
        let filteredChannel = filteredByPropertyType.filter(x => x.channel === chan);
        const label = `${chan} (${filteredChannel.length})`;
        return (
          <Tooltip title="View on workflow grid" key={label}>
            <Chip
              classes={{
                root: classes.compTypeChip,
                label: classes.label
              }}
              style={{
                backgroundColor: 'transparent',
                color: 'rgba(0, 0, 0, 0.54)',
                border: '1px solid rgba(0, 0, 0, 0.54)',
                margin: '5px'
              }}
              label={label}
              onClick={() => handleFilterChannelClick(filteredChannel)}></Chip>
          </Tooltip>
        )
      });
      if (distinctChannels.size > 1) {
        const allChannels = (
          <Tooltip title="View on workflow grid">
            <Chip
              classes={{
                root: classes.compTypeChip,
                label: classes.label
              }}
              style={{
                backgroundColor: 'transparent',
                color: 'rgba(0, 0, 0, 0.54)',
                border: '1px solid rgba(0, 0, 0, 0.54)',
                margin: '5px'
              }}
              label={`All (${filteredByPropertyType.length})`}
              onClick={() => handleFilterChannelClick(filteredByPropertyType)}></Chip>
          </Tooltip>
        )
        filteredDistinctChannels.push(allChannels);
      }
      return filteredDistinctChannels;
    }
    return null;
  }, [data]);

  useEffect(() => {
    if(channelWorkflowGridDataUpdate?.data) {
      Object.entries(channelWorkflowGridDataUpdate.data)
      .filter(pair => pair[1].type === 'remove')
      .forEach(pair => {
        const deletedOppId = parseInt(pair[0])
        const deletedOppImportId = pair[1].data.importId;
        const hasdeletedOppInSearch = data.some(v => v.resultType !== 'import' && v.id === deletedOppId);
        const hasImportWithDeletedOppInSearch = data.some(v => v.resultType === 'import' && v.id === deletedOppImportId);
        if(hasImportWithDeletedOppInSearch || hasdeletedOppInSearch) {
          debouncedOnSearchChange(searchBoxValue)
        }
      });
    }
  }, [channelWorkflowGridDataUpdate])

  return (
    <Drawer
      open={searchIsOpen}
      onClose={drawerOnClose}
      anchor="right"
      variant={'temporary'}
      classes={{
        paper: classes.drawerPaper
      }}
    >
      <div className={classes.searchContainer}>
        <Paper className={classes.searchBoxContainer}>
          <InputBase
            className={classes.input}
            placeholder="Search"
            inputProps={{ 'aria-label': 'search in app' }}
            value={searchBoxValue}
            onChange={onSearchChange}
            autoFocus
          />
          <Tooltip title='Parse from Clipboard'>
            <IconButton className={classes.icon} aria-label="pasteFromClipboard" onClick={pasteFromClipboard}>
              <PostAddIcon />
            </IconButton>
          </Tooltip>
          <IconButton className={classes.icon} aria-label="search">
            <SearchIcon />
          </IconButton>
        </Paper>
        <LoadingOverlayWrapper
          active={loading}
          message={loading ? 'Fetching search results...' : ""}
          className={classes.loadingOverlayWrapper}
          background={'rgba(242, 242, 242, 0.9)'}
        >
          <div className={classes.resultsContainer}>
            <Typography className={classes.searchResultCount} variant="overline" display="block" gutterBottom>
              <span>
                {`Displaying ${resultsLimited ? "top " : ""}${importCount + propertyCount + auctionCount} result(s)`}
                {(module === Constants.Module.UNDERWRITE || module === Constants.Module.POSTACQ) &&
                  <span>{filterChannels}</span>
                }
              </span>
              <span className={classes.searchButtons}>
                <Tooltip title={`Search for my last 100 priced ${module === Constants.Module.UNDERWRITE ? 'opps' : 'tasks'}`}>
                  <Chip
                    classes={{
                      root: classes.compTypeChip,
                      label: classes.label
                    }}
                    color={isSearchingLastPriced ? 'primary' : 'default'}
                    className={classNames(isSearchingLastPriced ? classes.lastPricedButtonActive : classes.lastPricedButton)}
                    label={'My Last 100 Priced'}
                    onClick={fetchSearchLastPricedByMe}
                  />
                </Tooltip>
              </span>
            </Typography>

            <div className={classes.overflow}>
              {cards}
            </div>
          </div>
        </LoadingOverlayWrapper>
      </div>
    </Drawer>
  )
}

const mapStateToProps = (state, ownProps) => {
  const module = getModuleByLocation(ownProps.location);
  const { searchIsOpen, data, searchValue, searchBoxValue, resultsLimited, importCount, propertyCount, auctionCount, loading, isSearchingLastPriced } = state.searchState[module];
  const channelWorkflowGridDataUpdate = module === Constants.Module.UNDERWRITE ? state.channelWorkflowState.channelWorkflowGridDataUpdate : state.postAcqWorkflowState.postAcqWorkflowGridDataUpdate;
  return {
    module,
    searchIsOpen,
    data,
    searchValue,
    searchBoxValue,
    resultsLimited,
    importCount,
    propertyCount,
    auctionCount,
    loading,
    isSearchingLastPriced,
    channelWorkflowGridDataUpdate,
  }
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const module = getModuleByLocation(ownProps.location);
  const gridStateUpdateAction = module === Constants.Module.UNDERWRITE ? setChannelWorkflowGridStateUpdate : setPostAcqWorkflowGridStateUpdate;
  return {
    setSearchState: state => dispatch(setSearchState(module, state)),
    fetchSearchLastPricedByMe: () => dispatch(fetchSearchLastPricedByMe(module)),
    appNotify: (message, variant, options) => dispatch(appNotify(message, variant, options)),
    setChannelWorkflowGridStateUpdate: (state, isUpdate) => dispatch(gridStateUpdateAction(state, isUpdate)),
    push: route => dispatch(push(route)),
  }
};

const styles = theme => ({
  searchContainer: {
    height: '100vh',
    width: 1000,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f2f2f2',
  },
  drawerPaper: {
    top: 'initial',
  },
  searchBoxContainer: {
    margin: 20,
    paddingRight: 5,
    display: 'flex',
  },
  loadingOverlayWrapper: {
    minHeight: 0,
    flexGrow: 1
  },
  resultsContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  icon: {
    padding: 0
  },
  input: {
    paddingLeft: '.5em',
    flexGrow: 1
  },
  searchResultCount: {
    margin: '0px 20px',
    display: 'flex',
    justifyContent: 'space-between'
  },
  overflow: {
    overflow: 'auto'
  },
  compTypeChip: {
    height: 18,
    color: '#fff'
  },
  label: {
    overflow: 'initial'
  },
  lastPricedButton: {
    backgroundColor: 'transparent',
    color: 'rgba(0, 0, 0, 0.54)',
    border: '1px solid rgba(0, 0, 0, 0.54)',
    margin: '5px'
  },
  lastPricedButtonActive: {
    border: '1px solid rgba(0, 0, 0, 0.54)',
    margin: '5px'
  }
});

export default compose(
  withRouter,
  withStyles(styles),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(SearchDrawer);
